import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Star, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StarRating = ({ value, onChange, label, testid }) => (
  <div className="space-y-2" data-testid={testid}>
    <p className="font-medium text-sm">{label}</p>
    <div className="flex items-center gap-2">
      {[1, 2, 3, 4, 5].map((s) => (
        <button
          key={s}
          type="button"
          onClick={() => onChange(s)}
          className="transition-transform hover:scale-110"
          data-testid={`${testid}-star-${s}`}
          aria-label={`Rate ${s} stars`}
        >
          <Star className={`w-9 h-9 ${s <= value ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/30'}`} />
        </button>
      ))}
      {value > 0 && <span className="ml-2 text-sm text-muted-foreground">{value} / 5</span>}
    </div>
  </div>
);

const SiteVisitFeedbackPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedVenueId = searchParams.get('venue') || '';

  const [loading, setLoading] = useState(true);
  const [venues, setVenues] = useState([]);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [venueId, setVenueId] = useState(preselectedVenueId);
  const [accompaniedBy, setAccompaniedBy] = useState('');
  const [brokerName, setBrokerName] = useState('');
  const [overall, setOverall] = useState(0);
  const [experience, setExperience] = useState('');
  const [suggestions, setSuggestions] = useState('');
  const [website, setWebsite] = useState(''); // honeypot — left empty by real visitors

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/venues`);
        if (!cancelled) setVenues(res.data || []);
      } catch (err) {
        if (!cancelled) setError('Could not load Clayworks centres. Please try again in a moment.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async () => {
    if (!venueId) {
      toast.error('Please select which centre you visited');
      return;
    }
    if (!accompaniedBy.trim()) {
      toast.error("Please tell us who accompanied you on the visit");
      return;
    }
    if (!overall) {
      toast.error('Please rate your overall experience');
      return;
    }
    if (!experience.trim()) {
      toast.error('Please share your experience during the visit');
      return;
    }
    if (!suggestions.trim()) {
      toast.error('Please share what we could improve');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/site-visit-feedback/submit`, {
        venue_id: venueId,
        accompanied_by_name: accompaniedBy.trim(),
        broker_name: brokerName.trim() || undefined,
        overall_rating: overall,
        experience_comments: experience.trim(),
        improvement_suggestions: suggestions.trim(),
        website,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="site-visit-feedback-error">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center space-y-3">
            <AlertTriangle className="w-12 h-12 mx-auto text-amber-500" />
            <h2 className="text-xl font-heading">Cannot open feedback form</h2>
            <p className="text-muted-foreground">{error}</p>
            <Button variant="outline" onClick={() => navigate('/')}>Back to Clayworks</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="site-visit-feedback-success">
        <Card className="max-w-md w-full">
          <CardContent className="p-10 text-center space-y-4">
            <CheckCircle2 className="w-16 h-16 mx-auto text-green-500" />
            <h2 className="text-2xl font-heading">Thank you!</h2>
            <p className="text-muted-foreground">Your feedback has been recorded. We use every response to make future site visits better.</p>
            <Button onClick={() => navigate('/')}>Back to Clayworks</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-10 px-4" data-testid="site-visit-feedback-page">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-8">
          <img src="/logo.webp" alt="Clayworks" className="h-10 w-auto mx-auto" />
          <p className="text-xs text-muted-foreground tracking-widest mt-2">SITE VISIT FEEDBACK</p>
        </div>

        <Card>
          <CardContent className="p-6 space-y-6">
            <div>
              <h2 className="text-2xl font-heading mb-1">How was your site visit?</h2>
              <p className="text-sm text-muted-foreground">A couple of quick questions — takes less than a minute.</p>
            </div>

            {/* Honeypot field — hidden from real visitors, bots tend to fill every field */}
            <input
              type="text"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              className="absolute left-[-9999px] w-px h-px opacity-0"
              aria-hidden="true"
            />

            <div className="space-y-5">
              <div className="space-y-2">
                <Label>Which Clayworks centre did you visit? *</Label>
                <Select value={venueId} onValueChange={setVenueId}>
                  <SelectTrigger data-testid="site-visit-venue-select"><SelectValue placeholder="Select centre" /></SelectTrigger>
                  <SelectContent>
                    {venues.map((v) => (
                      <SelectItem key={v.id} value={v.id}>{v.name}{v.city ? ` (${v.city})` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Who accompanied you and conducted the site visit? *</Label>
                <Input
                  value={accompaniedBy}
                  onChange={(e) => setAccompaniedBy(e.target.value)}
                  placeholder="Name of the person"
                  data-testid="site-visit-accompanied-by-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Which broker referred you? <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Input
                  value={brokerName}
                  onChange={(e) => setBrokerName(e.target.value)}
                  placeholder="Broker name, if any"
                  data-testid="site-visit-broker-input"
                />
              </div>

              <StarRating
                value={overall}
                onChange={setOverall}
                label="How would you rate your overall experience? *"
                testid="site-visit-rating-overall"
              />

              <div className="space-y-2">
                <Label>How was your experience during the visit? *</Label>
                <Textarea
                  rows={4}
                  value={experience}
                  onChange={(e) => setExperience(e.target.value)}
                  placeholder="Please share your feedback…"
                  maxLength={2000}
                  data-testid="site-visit-experience-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Anything we could improve for future site visits? *</Label>
                <Textarea
                  rows={4}
                  value={suggestions}
                  onChange={(e) => setSuggestions(e.target.value)}
                  placeholder="Your suggestions are valuable to us…"
                  maxLength={2000}
                  data-testid="site-visit-suggestions-input"
                />
              </div>
            </div>

            <Button onClick={handleSubmit} disabled={submitting} className="w-full" size="lg" data-testid="site-visit-feedback-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Submit Feedback
            </Button>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Clayworks Spaces Technologies Pvt Ltd
        </p>
      </div>
    </div>
  );
};

export default SiteVisitFeedbackPage;
